import {
  StackDivider,
  TableColumnHeaderProps,
  TableContainer,
  TableContainerProps,
  VStack,
} from '@chakra-ui/react';
import { useScheduler, useStableCallback } from '@main/shared/browser';
import { Nullable } from '@main/shared/types';
import { FilterMode } from '@main/shared/url-helpers';
import { isNonNullable } from '@main/shared/utils';
import {
  CellContext,
  ColumnDef,
  ColumnFiltersState,
  CoreOptions,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel as originalGetSortedRowModel,
  OnChangeFn,
  PaginationState,
  Row,
  RowModel,
  SortDirection,
  SortingState,
  Table as TanstackTable,
  TableMeta,
  Updater,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { memo, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { isSafari, useTableSettingsNamespace } from '../../utils';
import { TableHeaderAction } from '../actions/types';
import { useDrawer } from '../drawer';
import { ActiveRowProvider, useActiveRowState } from './active-row-context';
import {
  BulkActionsConfig,
  BulkEditableFieldConfig,
  BulkSelectionLoadingProvider,
  BulkSelectionToolbar,
  hasBulkChanges,
  selectionColumn,
  useDeselectRemovedRows,
  useHasBulkChanges,
  useRowSelectionState,
} from './bulk-actions';
import { FilterColumnMeta, getColumnCanGlobalFilter } from './filters';
import {
  emptyLegacyColumnIdMap,
  getLegacyColumnIdMap,
  resolveLegacyColumnIdEntries,
  resolveLegacyColumnIdUpdater,
  reuseLegacyColumnIdMap,
} from './legacy-column-ids';
import { ColumnTableAction, TableActionHandler, TableActionType } from './table-action';
import { TableCellColumnMeta } from './table-cell';
import { actionsColumnId, fakeColumnId, selectionColumnId } from './table-column-ids';
import { TableFilterResult } from './table-filter-result';
import { TableMenu } from './table-menu';
import { InternalTableMeta } from './table-meta';
import { TableEmptyFilterResult, TableEmptyState, TableLoadingState } from './table-render-states';
import {
  getUserConfigurableTableColumns,
  useGetTableSettingsStateFromTable,
} from './table-settings-state';
import { useTableColumnOrderForNamespace } from './use-table-column-order';
import { useTableColumnSizingForNamespace } from './use-table-column-sizing';
import { useTablePaginationForNamespace } from './use-table-pagination';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    name?: string;
    header?: TableColumnHeaderProps;
    cell?: TableCellColumnMeta<TData>;
    filter?: FilterColumnMeta<TData, TValue>;
    globalFilterFn?: (value: TValue, filterValue: string) => boolean;
    action?: ColumnTableAction<TData>;
    getGlobalFilterCondition?<TFilter>(filterValue: string): Nullable<TFilter>;
    getColumnFilterCondition?<TFilter>(
      filterMode: FilterMode,
      filterValue: unknown,
    ): Nullable<TFilter>;
    getColumnSort?<TSort>(sortDirection: SortDirection): TSort;
    isMultiline?: boolean;
    sizeUnit?: 'px' | 'fr';
    /** Determines if a given row is editable */
    editable?: CanEditGuard<TData, TValue>;
    /** Override to show/hide editable styles on cells */
    editableStyles?: boolean;
    /** Bulk edit config */
    bulkEdit?: BulkEditableFieldConfig<TData>;
    /** Bulk actions config */
    bulkActions?: BulkActionsConfig<TData>;
    /** Shared helper discriminator for downstream consumers like export */
    columnType?: TableColumnType;
    /** CSV export config */
    export?: TableColumnExportConfig<TData, TValue>;
    /** Earlier IDs of this column. Saved table state that refers to one of them is moved to this column's ID. */
    legacyIds?: readonly string[];
  }
}

export type CanEditGuard<TData, TValue = unknown> = (
  row: TData,
  context?: CellContext<TData, TValue>,
) => boolean;

/* eslint-disable @typescript-eslint/no-unnecessary-type-constraint */
const canEditEntity =
  <TData, TValue>(canEditGuard?: CanEditGuard<TData, TValue>) =>
  (entity: TData, context?: CellContext<TData, TValue>) =>
    canEditGuard?.(entity, context) ?? true;

const entity =
  <TData, TValue>(fn: CanEditGuard<TData, TValue>) =>
  (row: Row<TData>) =>
    fn(row.original);

const canEditEveryRow =
  <TData, TValue>(canEditGuard?: CanEditGuard<TData, TValue>) =>
  (rows: Row<TData>[]) =>
    rows.every(entity(canEditEntity(canEditGuard)));

export const getEditable =
  <TData, TValue>(edit?: { canEditGuard?: CanEditGuard<TData, TValue> }) =>
  (entity: TData, context?: CellContext<TData, TValue>): boolean =>
    !edit ? false : canEditEntity(edit.canEditGuard)(entity, context);

const getBulkEditable =
  <TData, TValue>(
    edit: { canEditGuard?: CanEditGuard<TData, TValue> },
    bulkEdit: { isDisabled?: boolean; useBulkChange?: unknown },
  ) =>
  (rows: Row<TData>[]) =>
    !bulkEdit.isDisabled && !!canEditEveryRow(edit.canEditGuard)(rows);

export const ifBulkEditable = <TData, TValue, TConfig>(
  edit: { canEditGuard?: CanEditGuard<TData, TValue> } | undefined,
  bulkEdit: { isDisabled?: boolean; useBulkChange?: unknown } | undefined,
  getConfig: (rows: Row<TData>[]) => TConfig,
) =>
  !edit || !bulkEdit || !bulkEdit.useBulkChange
    ? undefined
    : (rows: Row<TData>[]) => (getBulkEditable(edit, bulkEdit)(rows) ? getConfig(rows) : undefined);
/* eslint-enable @typescript-eslint/no-unnecessary-type-constraint */

// eslint-disable-next-line @typescript-eslint/no-empty-interface, @typescript-eslint/no-empty-object-type
export interface TableRegistry {}

export type TableEntity = TableRegistry extends { entities: infer E } ? E : string;

export type TableItem = {
  singular: string;
  plural: string;
};

export type TableRowReorderHandler<T> = (
  fromRow: Row<T>,
  toRow: Row<T>,
  table: TanstackTable<T>,
) => void;

export type TableRowClickHandler<T> = {
  (row: Row<T>): void;
  isSupported?: (row: Row<T>) => boolean;
};

export type TableHeaderElement = TableHeaderAction | JSX.Element;
export type TableVisibleColumnIds = string[];

export type TableColumnExportConfig<TData, TValue> = {
  label?: string;
  getValue?: (context: { row: TData; value: TValue | undefined }) => unknown;
};

export type TableColumnType =
  | 'avatar'
  | 'status'
  | 'tag'
  | 'relation'
  | 'text'
  | 'date'
  | 'number'
  | 'link'
  | 'email'
  | 'boolean';

export type TableProps<T> = TableContainerProps & {
  enableSavingFiltersAndSort?: boolean;
  hideToolbar?: boolean;
  data: T[];
  /**
   * Used for local storage keys for table configuration storage.
   * *YOU MUST ENSURE** this is unique across the app unless you intentionally
   * want to share the same table configuration across different tables.
   */
  id: string;
  isLoading?: boolean;
  // not possible to use `unknown` with typed accessors
  // https://github.com/TanStack/table/issues/4241
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: (ColumnDef<T, any> | undefined)[];
  isColumnsLoading?: boolean;
  defaultColumnVisibility?: VisibilityState;
  columnVisibility?: VisibilityState;
  /**
   * When `onColumnVisibilityChange` callback is provided, the table will display the UI
   * with which user can modify table column's visibility state.
   */
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  hasColumnVisibilityChanged?: boolean;
  defaultPageSize?: number;
  pageSizeOptions?: [number, ...number[]];
  onRowClick?: TableRowClickHandler<T>;
  enableRowSelection?: boolean | ((row: { original: T }) => boolean);
  /**
   * `columnFilters` should be validated before passing to the table:
   * passing filters without matching columnd ids / with invalid structure will throw.
   * `isColumnFilter` / `isValidColumnFilter` predicates can be used for parsing filters
   * before passing them to the table
   */
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  globalFilter?: string;
  onGlobalFilterChange?: OnChangeFn<string>;
  onFilteredDataChange?: (data: T[]) => void;
  onVisibleColumnIdsChange?: (columnIds: TableVisibleColumnIds) => void;
  itemName: TableItem;
  entity?: TableEntity;
  activeRow?: string;
  disableActiveRow?: boolean;
  getRowId?: CoreOptions<T>['getRowId'];
  rowCount?: number;
  hasNextPage?: boolean;
  manualFiltering?: boolean;
  manualPagination?: boolean;
  manualSorting?: boolean;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  meta?: TableMeta<T>;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  minColumnSize?: number;
  onRowReorder?: TableRowReorderHandler<T>;
  getSortedRowModel?(table: TanstackTable<T>): () => RowModel<T>;
  renderEmptyState?: ({ itemName }: { itemName: TableItem }) => ReactNode;
  bulkSelectionToolbarPortalRef?: React.RefObject<HTMLElement>;
  headerActions?: TableHeaderElement[];
};

const defaultDefaultColumnVisibility = {};

// A zero-width _fixed size_ column fixes a css issue with rendering fr-sized columns
const fakeColumn = {
  id: fakeColumnId,
  size: 0,
  enableResizing: false,
};

type NamespacedTableProps<T> = TableProps<T> & {
  settingsNamespace?: string;
};

function EditableTable<T>({
  settingsNamespace,
  data,
  id,
  isLoading,
  columns: unresolvedColumns,
  isColumnsLoading,
  itemName,
  defaultColumnVisibility = defaultDefaultColumnVisibility,
  columnVisibility,
  onColumnVisibilityChange,
  hasColumnVisibilityChanged,
  defaultPageSize = 25,
  pageSizeOptions = [10, 25, 50, 100],
  columnFilters,
  globalFilter,
  entity,
  activeRow: activeRowProp,
  disableActiveRow,
  onColumnFiltersChange,
  onGlobalFilterChange,
  onRowClick,
  onFilteredDataChange,
  onVisibleColumnIdsChange,
  getRowId,
  rowCount,
  hasNextPage,
  manualFiltering,
  manualPagination,
  manualSorting,
  pagination: paginationProp,
  onPaginationChange: onPaginationChangeProp,
  meta,
  sorting,
  onSortingChange,
  minColumnSize = 35,
  onRowReorder,
  getSortedRowModel,
  renderEmptyState,
  bulkSelectionToolbarPortalRef,
  enableSavingFiltersAndSort = true,
  hideToolbar = false,
  headerActions,
  enableRowSelection = true,
  ...props
}: NamespacedTableProps<T>) {
  const { t } = useTranslation('ui');
  disableActiveRow = useMemo(() => disableActiveRow ?? isSafari(), [disableActiveRow]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
  getRowId = useMemo(() => getRowId ?? ((row: any, i: number) => row.id ?? String(i)), [getRowId]);

  const tableName = testIdFromItemName(itemName.plural);
  const drawer = useDrawer();
  const columns = useMemo(() => unresolvedColumns.filter(isNonNullable), [unresolvedColumns]);
  const hasBulkActions = useHasBulkChanges(columns, data);

  const rowsWithSelectionEnabled = useMemo(
    () =>
      typeof enableRowSelection === 'function'
        ? data.filter((d) => enableRowSelection({ original: d })).length
        : enableRowSelection
          ? data.length
          : 0,
    [enableRowSelection, data],
  );
  const hasMoreThanOneSelectableRow = rowsWithSelectionEnabled > 1;

  const isRowSelectionEnabled = !!(
    hasMoreThanOneSelectableRow &&
    hasBulkActions &&
    enableRowSelection
  );

  const [rowSelection, setRowSelection, selectedRows] = useRowSelectionState();

  const [activeRow, setActiveRow] = useActiveRowState(activeRowProp, disableActiveRow);
  const { schedule: scheduleHideActiveRow, cancel: cancelHideActiveRow } = useScheduler({
    fn: useCallback(() => setActiveRow(false), [setActiveRow]),
    delayMs: 1500,
  });

  // Reusing an equal map keeps a resolved legacy sort's identity, which would otherwise reset the
  // page every time the columns change.
  const previousLegacyColumnIdMapRef = useRef(emptyLegacyColumnIdMap);
  const legacyColumnIdMap = useMemo(() => {
    const map = reuseLegacyColumnIdMap(
      previousLegacyColumnIdMapRef.current,
      isColumnsLoading ? emptyLegacyColumnIdMap : getLegacyColumnIdMap(columns),
    );
    previousLegacyColumnIdMapRef.current = map;
    return map;
  }, [columns, isColumnsLoading]);

  const resolvedSorting = useMemo(
    () => sorting && resolveLegacyColumnIdEntries(sorting, legacyColumnIdMap),
    [sorting, legacyColumnIdMap],
  );
  const onResolvedSortingChange = useStableCallback((updater: Updater<SortingState>) =>
    onSortingChange?.(resolveLegacyColumnIdUpdater(updater, legacyColumnIdMap)),
  );

  const { columnSizing, hasColumnSizingChanged, onColumnSizingChange } =
    useTableColumnSizingForNamespace(
      tableName,
      settingsNamespace,
      id,
      undefined,
      legacyColumnIdMap,
    );

  const { columnOrder, hasColumnOrderChanged, onColumnOrderChange } =
    useTableColumnOrderForNamespace(
      columns,
      !!isColumnsLoading,
      tableName,
      settingsNamespace,
      columnVisibility,
      defaultColumnVisibility,
      id,
    );

  const { pagination, onPaginationChange } = useTablePaginationForNamespace(
    tableName,
    settingsNamespace,
    id,
    defaultPageSize,
  );

  const hasMultilineCol = useMemo(() => columns.some((col) => col.meta?.isMultiline), [columns]);
  const internalMeta = useMemo<InternalTableMeta<T>>(
    () => ({ ...meta, hasMultilineCol }),
    [hasMultilineCol, meta],
  );
  const allTableColumns = useMemo(
    () => [
      fakeColumn as ColumnDef<T>,
      ...(isRowSelectionEnabled ? [selectionColumn as ColumnDef<T>] : []),
      ...columns,
    ],
    [columns, isRowSelectionEnabled],
  );

  const allColumnOrders = useMemo(
    () => [
      fakeColumnId,
      ...(isRowSelectionEnabled ? [selectionColumnId] : []),
      ...getUserConfigurableTableColumns(columnOrder.map((id) => ({ id }))).map(({ id }) => id),
    ],
    [columnOrder, isRowSelectionEnabled],
  );

  const globalFilterFn = useMemo(() => {
    const filterFns = new Map<string, (value: unknown, filterValue: string) => boolean>();

    allTableColumns.forEach((col) => {
      if (col.id && col.meta?.globalFilterFn) {
        filterFns.set(col.id, col.meta.globalFilterFn);
      }
    });

    return (row: Row<T>, columnId: string, filterValue: string) => {
      const fn = filterFns.get(columnId);
      if (!fn) {
        return true;
      }
      return fn(row.getValue(columnId), filterValue);
    };
  }, [allTableColumns]);

  const table = useReactTable({
    data,
    columns: allTableColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel ?? originalGetSortedRowModel(),
    getColumnCanGlobalFilter: getColumnCanGlobalFilter,
    globalFilterFn,
    autoResetPageIndex: false,
    enableRowSelection: isRowSelectionEnabled ? enableRowSelection : undefined,
    rowCount,
    pageCount: openEndedPageCount((paginationProp ?? pagination).pageIndex, hasNextPage),
    manualFiltering,
    manualPagination,
    manualSorting,
    initialState: {
      columnVisibility: {},
      pagination: { pageSize: pagination.pageSize },
      columnPinning: { right: [actionsColumnId] },
    },
    getRowId,
    enableMultiRowSelection: true,
    state: {
      ...(columnVisibility && { columnVisibility }),
      ...(globalFilter && { globalFilter }),
      ...(columnFilters && { columnFilters }),
      ...(resolvedSorting && { sorting: resolvedSorting }),
      pagination: paginationProp ?? pagination,
      columnSizing,
      columnOrder: allColumnOrders,
      rowSelection,
    },
    meta: internalMeta,
    columnResizeMode: 'onChange',
    defaultColumn: {
      size: undefined,
      minSize: minColumnSize,
      maxSize: undefined,
    },
    ...(onSortingChange && { onSortingChange: onResolvedSortingChange }),
    ...(onGlobalFilterChange && { onGlobalFilterChange }),
    ...(onColumnFiltersChange && { onColumnFiltersChange }),
    ...(onColumnVisibilityChange && { onColumnVisibilityChange }),
    onPaginationChange: onPaginationChangeProp ?? onPaginationChange,
    onColumnSizingChange,
    onColumnOrderChange,
    onRowSelectionChange: isRowSelectionEnabled ? setRowSelection : undefined,
  });
  const state = table.getState();
  const filteredRows = table.getFilteredRowModel().rows;
  const tableSettingsState = useGetTableSettingsStateFromTable(table);

  useDeselectRemovedRows({ table, rowSelection, setRowSelection });

  const actionHandler: TableActionHandler<T> = useCallback(
    (action, row) => {
      switch (action.type) {
        case TableActionType.HighlightRow:
          setActiveRow(row.id);
          break;
      }
    },
    [setActiveRow],
  );

  // reset pagination when filters or sorting change
  const { setPageIndex } = table;
  useEffect(() => {
    setPageIndex(0);
  }, [setPageIndex, state.globalFilter, state.columnFilters, state.sorting]);

  useEffect(() => {
    onFilteredDataChange && onFilteredDataChange(filteredRows.map((row) => row.original));
    // oxlint-disable-next-line react/exhaustive-deps
  }, [filteredRows]);

  const visibleColumns = useMemo(
    () => tableSettingsState.visibleColumns.map((column) => column.id),
    [tableSettingsState.visibleColumns],
  );
  const previousVisibleColumnsRef = useRef<TableVisibleColumnIds>();

  useEffect(() => {
    if (isEqualVisibleColumns(previousVisibleColumnsRef.current, visibleColumns)) {
      return;
    }

    previousVisibleColumnsRef.current = visibleColumns;
    onVisibleColumnIdsChange?.(visibleColumns);
  }, [onVisibleColumnIdsChange, visibleColumns]);

  // Highlight row opened in drawer
  useEffect(
    () =>
      entity && !disableActiveRow
        ? drawer.onDrawer((data) => {
            if (data?.entity === entity && data.entityId) {
              if (data.entityId !== activeRow) {
                cancelHideActiveRow();
                setActiveRow(data.entityId);
              }
            } else if (activeRow !== false) {
              scheduleHideActiveRow();
            }
          })
        : undefined,
    [
      entity,
      drawer,
      activeRow,
      setActiveRow,
      disableActiveRow,
      scheduleHideActiveRow,
      cancelHideActiveRow,
    ],
  );

  useEffect(() => {
    if (isLoading) return;

    const pagination = table.getState().pagination;
    const startingRow = pagination.pageSize * pagination.pageIndex;
    const rowCount = table.getRowCount();
    const mode = hasNextPage === undefined ? 'totalCount' : 'hasNextPage';

    if (mode === 'hasNextPage' && pagination.pageIndex > 0 && data.length === 0) {
      table.previousPage();
      return;
    }

    if (mode === 'totalCount' && rowCount && startingRow >= rowCount) {
      table.previousPage();
    }
  }, [data.length, isLoading, table, hasNextPage]);

  const shouldRenderBulkSelectionToolbar = !!isRowSelectionEnabled && selectedRows.length > 0;
  const isBulkSelectionToolbarRenderedInTable =
    shouldRenderBulkSelectionToolbar && !bulkSelectionToolbarPortalRef;

  const renderContent = () => {
    if (isLoading) {
      return (
        <TableLoadingState
          table={table}
          minColumnSize={minColumnSize}
          isReorderable={!!onRowReorder}
          reservesSelection={!!enableRowSelection && hasBulkChanges(columns, [])}
        />
      );
    }

    if (table.options.data.length === 0) {
      return renderEmptyState ? (
        renderEmptyState({ itemName })
      ) : (
        <TableEmptyState
          itemName={itemName}
          subHeading={t('table.empty.subheading', { item: itemName.singular })}
        />
      );
    }

    if (filteredRows.length === 0) {
      return <TableEmptyFilterResult itemName={itemName} />;
    }

    return (
      <TableFilterResult
        itemName={itemName}
        table={table}
        minColumnSize={minColumnSize}
        onRowClick={onRowClick}
        actionHandler={actionHandler}
        setColumnSizing={onColumnSizingChange}
        onRowReorder={onRowReorder}
        pageSizeOptions={pageSizeOptions}
        isBulkSelectionToolbarRenderedInTable={isBulkSelectionToolbarRenderedInTable}
        hasNextPage={hasNextPage}
      />
    );
  };

  return (
    <BulkSelectionLoadingProvider>
      <ActiveRowProvider value={activeRow}>
        <TableContainer
          minW={table.options.data.length || filteredRows.length ? props.minW : 'full'}
          {...props}
          data-testid={tableName}
          data-tableid={id}
          overflowX="visible"
          overflowY="visible"
          borderRadius="0 !important"
        >
          <VStack align="normal" spacing={0} divider={<StackDivider />}>
            {!hideToolbar && (
              <TableMenu
                table={table}
                tableName={tableName}
                settingsNamespace={settingsNamespace}
                tableId={id}
                enableSavingFiltersAndSort={enableSavingFiltersAndSort}
                hasColumnOrderChanged={hasColumnOrderChanged}
                hasColumnVisibilityChanged={!!hasColumnVisibilityChanged}
                hasColumnSizingChanged={hasColumnSizingChanged}
                isColumnsLoading={!!isColumnsLoading}
                headerActions={headerActions}
              />
            )}

            {renderContent()}
          </VStack>
        </TableContainer>
        {shouldRenderBulkSelectionToolbar && (
          <BulkSelectionToolbar
            table={table}
            tableName={tableName}
            selectedItems={selectedRows}
            portalRef={bulkSelectionToolbarPortalRef}
          />
        )}
      </ActiveRowProvider>
    </BulkSelectionLoadingProvider>
  );
}

function testIdFromItemName(name: string) {
  return `${name.replace(/ /g, '-')}-table`;
}

function isEqualVisibleColumns(
  previousVisibleColumns: TableVisibleColumnIds | undefined,
  visibleColumns: TableVisibleColumnIds,
) {
  return (
    previousVisibleColumns?.length === visibleColumns.length &&
    previousVisibleColumns.every((columnId, index) => columnId === visibleColumns[index])
  );
}

function DefaultTable<T>(props: TableProps<T>) {
  const settingsNamespace = useTableSettingsNamespace();

  return <EditableTable {...props} settingsNamespace={settingsNamespace} />;
}

function TableWithExplicitSettingsNamespace<T>(
  props: TableProps<T> & {
    settingsNamespace: string;
  },
) {
  return <EditableTable {...props} />;
}

// react's memo does not preserve generic component's type
// https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087
export const Table = memo(DefaultTable) as typeof DefaultTable;
export const TableWithSettingsNamespace = memo(
  TableWithExplicitSettingsNamespace,
) as typeof TableWithExplicitSettingsNamespace;

// Tanstack clamps setPageIndex by pageCount-1. In hasNext mode we don't know totals,
// so expose "one more page" when next exists, current otherwise. Returns undefined
// for totalCount mode so tanstack derives pageCount from rowCount.
function openEndedPageCount(pageIndex: number, hasNextPage: boolean | undefined) {
  if (hasNextPage === undefined) return undefined;
  return pageIndex + (hasNextPage ? 2 : 1);
}
